// The "assign a program" list on a client's Programs tab, split into labelled groups.
//
// It used to be one flat list of every program the signed-in user can read that is not a client
// copy. A coach reads the curated templates plus their own programs, so for them that was short.
// A super_admin reads everything, so a program any coach built for themselves sat among the
// curated templates with nothing to tell it apart.
//
// The split follows the Programs Library screen (ProgramsList.jsx): a coach's own programs are
// theirs even when flagged as templates; for everyone else "template" means the curated library.
//
// The template library is long (over 110 since the core set was added), so it is not one group but
// several: the core set by where the client trains and what they are after, then "Extra programs",
// the templates nobody is given automatically (templateLibrary.js). The signed-in user's own programs come first, because there are few of
// them and they would otherwise sit below a hundred templates.
//
// An archived program has been withdrawn and is never offered.
import { groupTemplateLibrary, isArchived } from './templateLibrary';

export const ASSIGN_GROUP_LABELS = {
  mine: 'My programs',
  others: 'Built by other coaches',
};

/**
 * programs      rows from `programs` (id, name, is_template, is_client_copy, created_by, archived_at)
 * myAuthId      the signed-in user's auth id
 * role          the signed-in user's role
 * builderNames  { [auth id]: name } for whoever built the programs, where known
 * excludeId     a program to leave out (the one the client is already on)
 *
 * Returns [{ key, label, options: [{ id, label }] }], empty groups left out. "My programs" and
 * "Built by other coaches" keep the order the programs were given in; the template groups are in
 * the library's own order.
 */
export const groupAssignablePrograms = (
  programs,
  { myAuthId = null, role = null, builderNames = {}, excludeId = null } = {}
) => {
  const mine = [];
  const others = [];
  const templates = [];

  for (const program of programs || []) {
    if (program.is_client_copy || program.id === excludeId || isArchived(program)) continue;
    const builtByMe = !!myAuthId && program.created_by === myAuthId;

    if (builtByMe && (role === 'coach' || !program.is_template)) {
      mine.push({ id: program.id, label: program.name });
    } else if (program.is_template) {
      templates.push(program);
    } else {
      const builder = builderNames[program.created_by];
      others.push({ id: program.id, label: builder ? `${program.name} (${builder})` : program.name });
    }
  }

  return [
    { key: 'mine', label: ASSIGN_GROUP_LABELS.mine, options: mine },
    { key: 'others', label: ASSIGN_GROUP_LABELS.others, options: others },
    ...groupTemplateLibrary(templates).map((group) => ({
      key: `templates-${group.key}`,
      label: group.label,
      options: group.programs.map((program) => ({ id: program.id, label: program.name })),
    })),
  ].filter((group) => group.options.length > 0);
};
